import React, { useContext, useEffect, useState, useMemo } from "react";
import { TranslatorContext } from "../../../context/Translator";
import { Row, Col, Card, Modal, Form, Spinner } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import { LabelFieldComponent, LabelTextareaComponent } from "../../../components/fields";
import { ButtonComponent } from "../../../components/elements";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import Swal from 'sweetalert2';
import { FaPlus } from "react-icons/fa";

export default function PurchaseCreatePage() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();
    const AuthToken = localStorage.getItem("authToken");

    const [loading, setLoading] = useState(true);
    const [vendors, setVendors] = useState([]);
    const [products, setProducts] = useState([]);
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [errors, setErrors] = useState({});
    const [productStock, setProductStock] = useState({});
    const [forceUpdate, setForceUpdate] = useState(0);
    const [selectedVendorBalance, setSelectedVendorBalance] = useState(0);
    const [selectedVendorName, setSelectedVendorName] = useState('');
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [currentProductRowIndex, setCurrentProductRowIndex] = useState(null); // Add this

    // Vendor Modal States
    const [showVendorModal, setShowVendorModal] = useState(false);
    const [vendorForm, setVendorForm] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
        img: null
    });
    const [vendorErrors, setVendorErrors] = useState({});
    const [vendorLoading, setVendorLoading] = useState(false);

    const [showProductModal, setShowProductModal] = useState(false);
    const [productForm, setProductForm] = useState({
        name: '',
        cat_id: '',
        vendor_id: '',
    });
    const [productErrors, setProductErrors] = useState({});
    const [productLoading, setProductLoading] = useState(false);
    const [categories, setCategories] = useState([]);

    // Get today's date in YYYY-MM-DD format
    const getTodayDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const [formData, setFormData] = useState({
        vendor_id: "",
        purchase_date: getTodayDate(),
        img: null,
        notes: "",
        tax: "",
        products: [{
            product_id: "",
            quantity: "",
            extra_quantity: "",
            purchase_price: "",
            sale_price: "",
            online_price: ""
        }],
        payments: [{ payment_method_id: "", amount: "", payment_date: getTodayDate() }],
    });

    useEffect(() => {
        fetchInitialData();
    }, [AuthToken]);

    // Filter products based on selected vendor
    useEffect(() => {
        if (formData.vendor_id) {
            const selectedVendorData = vendors.find(v => v.id == formData.vendor_id);
            if (selectedVendorData && selectedVendorData.products) {
                setProducts(selectedVendorData.products);
                setSelectedVendorBalance(selectedVendorData.balance || 0);
                setSelectedVendorName(selectedVendorData.company_name || selectedVendorData.name);
                setSelectedVendor(selectedVendorData);
            } else {
                setProducts([]);
                setSelectedVendorBalance(0);
                setSelectedVendorName('');
                setSelectedVendor(null);
            }
        } else {
            setProducts([]);
            setSelectedVendorBalance(0);
            setSelectedVendorName('');
            setSelectedVendor(null);
        }
    }, [formData.vendor_id, vendors]);

    const fetchInitialData = () => {
        axios
            .get(`${process.env.REACT_APP_API_URL}/purchases-create`, {
                headers: { Authorization: `Bearer ${AuthToken}` },
            })
            .then((res) => {
                setVendors(res.data.vendors);
                if (res.data.payment_methods) setPaymentMethods(res.data.payment_methods);
                else {
                    axios.get(`${process.env.REACT_APP_API_URL}/payment-methods`, {
                        headers: { Authorization: `Bearer ${AuthToken}` },
                    }).then(pmRes => setPaymentMethods(pmRes.data.payment_methods || []));
                }
            })
            .catch((err) => console.error(err))
            .finally(() => setLoading(false));
        fetchCategories();
    };

    const fetchCategories = () => {
        axios
            .get(`${process.env.REACT_APP_API_URL}/products-create`, {
                headers: { Authorization: `Bearer ${AuthToken}` },
            })
            .then((res) => {
                setCategories(res.data.categories || []);
            })
            .catch((err) => console.error(err));
    };

    // Product Modal Handlers
    const openProductModal = (rowIndex = null) => {
        setCurrentProductRowIndex(rowIndex);
        setProductForm({
            name: '',
            cat_id: '',
            vendor_id: formData.vendor_id || '',
        });
        setProductErrors({});
        setShowProductModal(true);
    };

    const handleProductFormChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        setProductForm(prev => ({
            ...prev,
            [name]: type === 'file' ? files[0] : (type === 'checkbox' ? checked : value)
        }));
        if (productErrors[name]) setProductErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleProductSubmit = async (e) => {
        e.preventDefault();
        setProductLoading(true);
        setProductErrors({});

        try {
            const submitData = {
                name: productForm.name,
                cat_id: productForm.cat_id,
                vendor_id: productForm.vendor_id
            };

            const response = await axios.post(`${process.env.REACT_APP_API_URL}/products`, submitData, {
                headers: {
                    Authorization: `Bearer ${AuthToken}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.status === 200 || response.status === 201) {
                const newProduct = response.data.product || response.data.data;

                if (newProduct && newProduct.id) {
                    // Refresh vendors data
                    const vendorsRes = await axios.get(`${process.env.REACT_APP_API_URL}/purchases-create`, {
                        headers: { Authorization: `Bearer ${AuthToken}` },
                    });

                    setVendors(vendorsRes.data.vendors);

                    const currentVendor = vendorsRes.data.vendors.find(v => v.id == formData.vendor_id);

                    if (currentVendor && currentVendor.products) {
                        // Update products state
                        setProducts([...currentVendor.products]);

                        // CRITICAL: Force multiple re-renders
                        setForceUpdate(prev => prev + 1);

                        // Update formData to select new product
                        setFormData(prev => {
                            const updatedProducts = [...prev.products];

                            if (currentProductRowIndex !== null && updatedProducts[currentProductRowIndex]) {
                                updatedProducts[currentProductRowIndex] = {
                                    ...updatedProducts[currentProductRowIndex],
                                    product_id: String(newProduct.id)
                                };
                            } else {
                                const emptyRowIndex = updatedProducts.findIndex(p => !p.product_id);
                                if (emptyRowIndex !== -1) {
                                    updatedProducts[emptyRowIndex].product_id = String(newProduct.id);
                                }
                            }

                            return { ...prev, products: updatedProducts };
                        });

                        // Another force update after formData update
                        setTimeout(() => {
                            setForceUpdate(prev => prev + 1);
                        }, 50);
                    }

                    setShowProductModal(false);
                    setCurrentProductRowIndex(null);

                    Swal.fire({
                        toast: true,
                        position: 'top-end',
                        icon: 'success',
                        title: t('Product added successfully!'),
                        showConfirmButton: false,
                        timer: 2000,
                        timerProgressBar: true,
                    });
                }
            }
        } catch (error) {
            console.error("Error:", error);
            if (error.response?.status === 422) {
                setProductErrors(error.response.data.errors || {});
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'error',
                    title: t('Please check the form for errors'),
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true,
                });
            } else {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'error',
                    title: t('Failed to add product'),
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true,
                });
            }
        } finally {
            setProductLoading(false);
        }
    };

    // Vendor Modal Handlers
    const openVendorModal = () => {
        setVendorForm({ name: '', email: '', phone: '', address: '', img: null });
        setVendorErrors({});
        setShowVendorModal(true);
    };

    const handleVendorChange = (e) => {
        const { name, value, type, files } = e.target;
        setVendorForm(prev => ({ ...prev, [name]: type === 'file' ? files[0] : value }));
        if (vendorErrors[name]) setVendorErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleVendorSubmit = async (e) => {
        e.preventDefault();
        setVendorLoading(true);
        setVendorErrors({});

        try {
            const formData = new FormData();
            for (let key in vendorForm) {
                if (vendorForm[key] !== null && vendorForm[key] !== '') {
                    formData.append(key, vendorForm[key]);
                }
            }

            const response = await axios.post(`${process.env.REACT_APP_API_URL}/vendors-store`, formData, {
                headers: {
                    Authorization: `Bearer ${AuthToken}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (response.data.success) {
                // Refresh vendors list
                const vendorsRes = await axios.get(`${process.env.REACT_APP_API_URL}/purchases-create`, {
                    headers: { Authorization: `Bearer ${AuthToken}` },
                });
                setVendors(vendorsRes.data.vendors);

                // Auto-select the newly created vendor
                const newVendor = response.data.vendor || response.data.data;
                if (newVendor && newVendor.id) {
                    setFormData(prev => ({ ...prev, vendor_id: newVendor.id }));
                }

                setShowVendorModal(false);
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: t('Vendor added successfully!'),
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true,
                });
            }
        } catch (error) {
            if (error.response?.status === 422) {
                setVendorErrors(error.response.data.errors || {});
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'error',
                    title: t('Please check the form for errors'),
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true,
                });
            } else {
                console.error(error);
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'error',
                    title: t('Failed to add vendor'),
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true,
                });
            }
        } finally {
            setVendorLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "purchase_date") {
            let newPurchaseDate = value;
            if (!newPurchaseDate || newPurchaseDate === '') {
                newPurchaseDate = getTodayDate();
            }

            setFormData((prev) => ({
                ...prev,
                [name]: newPurchaseDate,
                payments: prev.payments.map(payment => ({
                    ...payment,
                    payment_date: ''
                }))
            }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }

        if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleProductChange = (index, e) => {
        const { name, value } = e.target;
        const updatedProducts = [...formData.products];
        const newValue = value === '' ? '' : (name === "quantity" || name === "extra_quantity" || name.includes("price") ? Number(value) : value);
        updatedProducts[index][name] = newValue;
        setFormData((prev) => ({ ...prev, products: updatedProducts }));
    };

    const updatePreviousPrice = async (index, fieldName, productId) => {
        if (!productId) return;

        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/products/${productId}/last-prices`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            const updatedProducts = [...formData.products];
            const priceValue = res.data[fieldName] || 0;
            updatedProducts[index][fieldName] = priceValue;

            setFormData(prevForm => ({ ...prevForm, products: updatedProducts }));
            setForceUpdate(prev => prev + 1);

            setPreviousPrices(prev => ({
                ...prev,
                [productId]: { ...prev[productId], [fieldName]: priceValue }
            }));
        } catch (err) {
            console.error(err);
        }
    };

    const handleImageChange = (e) => {
        setFormData((prev) => ({
            ...prev,
            img: e.target.files[0],
        }));
    };

    const grandTotal = useMemo(() => {
        const productsTotal = formData.products.reduce((acc, p) => {
            const quantity = Number(p.quantity) || 0;
            const purchasePrice = Number(p.purchase_price) || 0;
            return acc + (quantity * purchasePrice);
        }, 0);
        const tax = Number(formData.tax) || 0;
        return productsTotal + tax;
    }, [formData.products, formData.tax]);

    const totalPaid = useMemo(() => {
        return formData.payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    }, [formData.payments]);

    const getRemainingAmount = () => {
        return Math.max(0, grandTotal - totalPaid);
    };

    const getTotalBalanceWithCurrentPurchase = () => {
        return selectedVendorBalance + grandTotal;
    };

    const handlePaymentChange = (index, e) => {
        const { name, value } = e.target;

        if (name === 'amount') {
            let newAmount = value === '' ? '' : Number(value);
            if (newAmount !== '' && (isNaN(newAmount) || newAmount < 0)) newAmount = 0;

            const updatedPayments = [...formData.payments];
            updatedPayments[index] = {
                ...updatedPayments[index],
                [name]: newAmount === '' ? '' : newAmount
            };
            setFormData(prev => ({ ...prev, payments: updatedPayments }));
        } else {
            const updatedPayments = [...formData.payments];
            updatedPayments[index] = {
                ...updatedPayments[index],
                [name]: value
            };
            setFormData(prev => ({ ...prev, payments: updatedPayments }));
        }
    };

    const addProductRow = () =>
        setFormData((prev) => ({
            ...prev,
            products: [...prev.products, {
                product_id: "",
                quantity: "",
                extra_quantity: "",
                purchase_price: "",
                sale_price: "",
                online_price: ""
            }],
        }));

    const removeProductRow = (index) => {
        const updatedProducts = formData.products.filter((_, i) => i !== index);
        setFormData((prev) => ({ ...prev, products: updatedProducts }));
    };

    const addPaymentRow = () => {
        const remaining = getRemainingAmount();

        if (remaining <= 0) {
            Swal.fire({
                icon: 'info',
                title: t("Info"),
                text: t("Purchase is already fully paid"),
                confirmButtonText: t("OK"),
            });
            return;
        }

        setFormData(prev => ({
            ...prev,
            payments: [...prev.payments, {
                payment_method_id: "",
                amount: remaining,
                payment_date: getTodayDate()
            }]
        }));
    };

    const removePaymentRow = (index) => {
        if (formData.payments.length === 1) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: t("At least one payment row is required"),
                showConfirmButton: false,
                timer: 2000,
                timerProgressBar: true,
            });
            return;
        }

        const updatedPayments = formData.payments.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, payments: updatedPayments }));
    };

    const handleSubmit = () => {
        setErrors({});

        if (!formData.vendor_id) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: t("Please select a vendor"),
                showConfirmButton: false,
                timer: 2500,
                timerProgressBar: true,
            });
            return;
        }

        const hasValidProduct = formData.products.some(prod =>
            prod.product_id && prod.quantity && prod.quantity !== '' &&
            prod.purchase_price && prod.purchase_price !== ''
        );

        if (!hasValidProduct) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: t("Please add at least one valid product with quantity and purchase price"),
                showConfirmButton: false,
                timer: 2500,
                timerProgressBar: true,
            });
            return;
        }

        const submitData = {
            ...formData,
            products: formData.products.map(prod => ({
                ...prod,
                quantity: prod.quantity === '' ? 0 : Number(prod.quantity),
                extra_quantity: prod.extra_quantity === '' ? 0 : Number(prod.extra_quantity),
                purchase_price: prod.purchase_price === '' ? 0 : Number(prod.purchase_price),
                sale_price: prod.sale_price === '' ? 0 : Number(prod.sale_price),
                online_price: prod.online_price === '' ? 0 : Number(prod.online_price),
            })),
            payments: formData.payments.map(payment => ({
                ...payment,
                amount: payment.amount === '' ? 0 : Number(payment.amount),
            })),
            tax: formData.tax === '' ? 0 : Number(formData.tax)
        };

        const invalidPayments = submitData.payments.some(p => Number(p.amount) > 0 && !p.payment_method_id);
        if (invalidPayments) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: t("Please select payment method for all payments with amount greater than 0"),
                showConfirmButton: false,
                timer: 2500,
                timerProgressBar: true,
            });
            return;
        }

        const invalidPaymentDates = submitData.payments.some(p => Number(p.amount) > 0 && !p.payment_date);
        if (invalidPaymentDates) {
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'warning',
                title: t("Please select payment date for all payments with amount greater than 0"),
                showConfirmButton: false,
                timer: 2500,
                timerProgressBar: true,
            });
            return;
        }

        axios
            .post(`${process.env.REACT_APP_API_URL}/purchases`, submitData, {
                headers: { Authorization: `Bearer ${AuthToken}` },
            })
            .then(() => {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: t("Purchase created successfully!"),
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true,
                });
                navigate("/purchase-list");
            })
            .catch((err) => {
                if (err.response?.status === 422) setErrors(err.response.data.errors);
                else console.error(err);
            });
    };

    useEffect(() => {
        if (formData.payments.length === 1 && grandTotal > 0) {
            const firstPaymentAmount = formData.payments[0]?.amount;
            if (grandTotal > 0 && firstPaymentAmount !== grandTotal) {
                setFormData(prev => ({
                    ...prev,
                    payments: [{
                        ...prev.payments[0],
                        amount: grandTotal,
                        payment_date: prev.payments[0].payment_date || getTodayDate()
                    }]
                }));
            }
        }
    }, [grandTotal]);

    useEffect(() => {
        console.log("Products state changed:", products);
    }, [products]);

    const isOnlineProduct = (productId) => {
        const product = products.find(p => p.id === productId);
        return product?.is_online === 1;
    };

    const [previousPrices, setPreviousPrices] = useState({});

    if (loading) return <PageLayout>{t("Loading...")}</PageLayout>;

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <Card className="mb-4 p-4">
                        <Card.Body>
                            <ButtonComponent
                                className="btn btn-secondary mb-4"
                                onClick={() => navigate("/purchase-list")}
                                text={t("Back to Purchases")}
                            />

                            <Row className="mb-4">
                                <Col xs={12} md={4}>
                                    <LabelFieldComponent
                                        type="date"
                                        label={t("Purchase Date")}
                                        name="purchase_date"
                                        value={formData.purchase_date}
                                        onChange={handleChange}
                                        error={errors.purchase_date}
                                        style={{ width: "100%" }}
                                    />
                                </Col>
                                <Col xs={12} md={4}>
                                    <label style={{ fontWeight: 'bold', marginBottom: '4px', display: 'block' }}>
                                        {t("Vendor")} <span className="text-danger">*</span>
                                    </label>
                                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <div style={{ flex: 1 }}>
                                            <Select
                                                options={vendors.map(vendor => ({
                                                    value: vendor.id,
                                                    label: vendor.company_name,
                                                    balance: vendor.balance
                                                }))}
                                                value={vendors
                                                    .filter(vendor => vendor.id === formData.vendor_id)
                                                    .map(vendor => ({ value: vendor.id, label: vendor.company_name }))}
                                                onChange={(selected) => {
                                                    const newVendorId = selected ? selected.value : "";
                                                    const selectedVendorData = vendors.find(v => v.id == newVendorId);
                                                    setFormData(prev => {
                                                        const updated = { ...prev, vendor_id: newVendorId };
                                                        const vendorProducts = selectedVendorData ? selectedVendorData.products.map(p => p.id) : [];
                                                        updated.products = prev.products.map(prod => {
                                                            if (!vendorProducts.includes(prod.product_id)) {
                                                                return { ...prod, product_id: "" };
                                                            }
                                                            return prod;
                                                        });
                                                        return updated;
                                                    });
                                                }}
                                                isClearable
                                                placeholder={t("Select Vendor")}
                                                styles={{
                                                    container: (provided) => ({ ...provided, width: "100%" }),
                                                    control: (provided) => ({ ...provided, height: "38px" }),
                                                }}
                                            />
                                        </div>
                                        <ButtonComponent
                                            className="btn btn-primary"
                                            onClick={openVendorModal}
                                            style={{ height: "38px", width: "38px", padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                            text={<FaPlus />}
                                        />
                                    </div>
                                    {errors.vendor_id && <span style={{ color: 'red', fontSize: '0.8rem' }}>{errors.vendor_id}</span>}
                                </Col>
                                <Col xs={12} md={4}>
                                    <LabelFieldComponent
                                        type="file"
                                        label={t("Upload Image")}
                                        name="img"
                                        onChange={handleImageChange}
                                        error={errors.img}
                                        style={{ width: "100%" }}
                                    />
                                </Col>
                            </Row>

                            {formData.vendor_id && (
                                <Row className="mb-4">
                                    <Col xs={12} md={4}>
                                        <div style={{
                                            padding: '15px',
                                            backgroundColor: '#f8f9fa',
                                            borderRadius: '8px',
                                            border: '1px solid #dee2e6'
                                        }}>
                                            <small className="text-muted">{t("Previous Balance")}</small>
                                            <div>
                                                <strong style={{ fontSize: '1.2rem', color: selectedVendorBalance >= 0 ? '#28a745' : '#dc3545' }}>
                                                    {selectedVendorBalance}
                                                </strong>
                                            </div>
                                            <small className="text-muted">{selectedVendorName}</small>
                                        </div>
                                    </Col>
                                    <Col xs={12} md={4}>
                                        <div style={{
                                            padding: '15px',
                                            backgroundColor: '#007bff',
                                            borderRadius: '8px',
                                            color: 'white'
                                        }}>
                                            <small style={{ opacity: 0.9 }}>{t("Current Purchase")}</small>
                                            <div>
                                                <strong style={{ fontSize: '1.2rem' }}>
                                                    {grandTotal}
                                                </strong>
                                            </div>
                                            <small style={{ opacity: 0.9 }}>{t("Including Tax")}</small>
                                        </div>
                                    </Col>
                                    <Col xs={12} md={4}>
                                        <div style={{
                                            padding: '15px',
                                            backgroundColor: '#ffc107',
                                            borderRadius: '8px',
                                            color: '#856404'
                                        }}>
                                            <small style={{ opacity: 0.9 }}>{t("Total Balance After Purchase")}</small>
                                            <div>
                                                <strong style={{ fontSize: '1.2rem' }}>
                                                    {getTotalBalanceWithCurrentPurchase()}
                                                </strong>
                                            </div>
                                            <small style={{ opacity: 0.9 }}>{t("Previous + Current Purchase")}</small>
                                        </div>
                                    </Col>
                                </Row>
                            )}

                            {/* Products Section */}
                            <h4 className="mb-3">{t("Products")} <span className="text-danger">*</span></h4>
                            {formData.products.map((prod, index) => (
                                <Card
                                    className="mb-3 p-3"
                                    key={index}
                                    style={{ borderColor: "#ddd", backgroundColor: "#f9f9f9", borderRadius: "6px" }}
                                >
                                    <Row className="align-items-stretch">
                                        <Col xl={1} className="d-flex flex-column">
                                            <ButtonComponent
                                                className="btn btn-danger btn-sm mt-4"
                                                onClick={() => removeProductRow(index)}
                                                text="X"
                                            />
                                        </Col>

                                        <Col xl={3} className="d-flex flex-column">
                                            <label className="fw-bold mb-1">{t("Product")} <span className="text-danger">*</span></label>
                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                <div style={{ flex: 1 }}>
                                                    <Select
                                                        key={`product-select-${forceUpdate}-${index}`}
                                                        options={products.map(product => ({
                                                            value: product.id,
                                                            label: product.name
                                                        }))}
                                                        value={
                                                            products.find(product => product.id == prod.product_id)
                                                                ? { value: prod.product_id, label: products.find(p => p.id == prod.product_id)?.name }
                                                                : null
                                                        }
                                                        onChange={async (selected) => {
                                                            const updatedProducts = [...formData.products];
                                                            updatedProducts[index].product_id = selected ? selected.value : "";
                                                            setFormData(prev => ({ ...prev, products: updatedProducts }));

                                                            if (selected) {
                                                                try {
                                                                    const res = await axios.get(
                                                                        `${process.env.REACT_APP_API_URL}/products/${selected.value}/last-prices`,
                                                                        { headers: { Authorization: `Bearer ${AuthToken}` } }
                                                                    );
                                                                    setPreviousPrices(prev => ({
                                                                        ...prev,
                                                                        [selected.value]: res.data
                                                                    }));
                                                                    setProductStock(prev => ({
                                                                        ...prev,
                                                                        [selected.value]: res.data.stock_quantity || 0
                                                                    }));
                                                                } catch (err) {
                                                                    console.error(err);
                                                                }
                                                            }
                                                        }}
                                                        isClearable
                                                        placeholder={t("Select Product")}
                                                    />
                                                </div>
                                                <ButtonComponent
                                                    className="btn btn-primary"
                                                    onClick={() => openProductModal(index)}
                                                    style={{ height: "38px", width: "38px", padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                                    text={<FaPlus />}
                                                />
                                            </div>
                                        </Col>

                                        <Col xl={1} className="d-flex flex-column">
                                            <label className="fw-bold mb-1">{t("Qty")} <span className="text-danger">*</span></label>
                                            <input
                                                type="number"
                                                name="quantity"
                                                value={prod.quantity}
                                                min={0}
                                                placeholder={t("Qty")}
                                                onChange={(e) => handleProductChange(index, e)}
                                                style={{
                                                    width: "100%",
                                                    height: "38px",
                                                    padding: '6px 8px',
                                                    borderRadius: '4px',
                                                    border: '1px solid #ced4da'
                                                }}
                                            />
                                        </Col>

                                        <Col xl={1} className="d-flex flex-column">
                                            <label className="fw-bold mb-1">{t("Gift Qty")}</label>
                                            <input
                                                type="number"
                                                name="extra_quantity"
                                                value={prod.extra_quantity}
                                                min={0}
                                                placeholder={t("Gift Qty")}
                                                onChange={(e) => handleProductChange(index, e)}
                                                style={{
                                                    width: "100%",
                                                    height: "38px",
                                                    padding: '6px 8px',
                                                    borderRadius: '4px',
                                                    border: '1px solid #ced4da'
                                                }}
                                            />
                                        </Col>

                                        <Col xl={2} className="d-flex flex-column">
                                            <label className="fw-bold mb-1">{t("Purchase Price")} <span className="text-danger">*</span></label>
                                            <input
                                                type="number"
                                                name="purchase_price"
                                                value={prod.purchase_price}
                                                min={0}
                                                step="0.01"
                                                placeholder={t("Price")}
                                                onChange={(e) => handleProductChange(index, e)}
                                                style={{
                                                    width: "100%",
                                                    height: "38px",
                                                    padding: '6px 8px',
                                                    borderRadius: '4px',
                                                    border: '1px solid #ced4da'
                                                }}
                                            />
                                            <label style={{ fontSize: "0.7rem", marginTop: "4px", cursor: "pointer" }}>
                                                <input
                                                    type="checkbox"
                                                    style={{ marginRight: "3px" }}
                                                    onChange={async (e) => {
                                                        if (e.target.checked && prod.product_id) {
                                                            await updatePreviousPrice(index, 'purchase_price', prod.product_id);
                                                        }
                                                    }}
                                                />
                                                {t("Prev")}
                                            </label>
                                        </Col>

                                        <Col xl={2} className="d-flex flex-column">
                                            <label className="fw-bold mb-1">{t("Sale Price")} <span className="text-danger">*</span></label>
                                            <input
                                                type="number"
                                                name="sale_price"
                                                value={prod.sale_price}
                                                min={0}
                                                step="0.01"
                                                placeholder={t("Sale Price")}
                                                onChange={(e) => handleProductChange(index, e)}
                                                style={{
                                                    width: "100%",
                                                    height: "38px",
                                                    padding: '6px 8px',
                                                    borderRadius: '4px',
                                                    border: '1px solid #ced4da'
                                                }}
                                            />
                                            <label style={{ fontSize: "0.7rem", marginTop: "4px", cursor: "pointer" }}>
                                                <input
                                                    type="checkbox"
                                                    style={{ marginRight: "3px" }}
                                                    onChange={async (e) => {
                                                        if (e.target.checked && prod.product_id) {
                                                            await updatePreviousPrice(index, 'sale_price', prod.product_id);
                                                        }
                                                    }}
                                                />
                                                {t("Prev")}
                                            </label>
                                        </Col>

                                        <Col xl={1} className="d-flex flex-column">
                                            <label className="fw-bold mb-1">{t("Total")}</label>
                                            <input
                                                type="number"
                                                value={((Number(prod.quantity) || 0) * (Number(prod.purchase_price) || 0))}
                                                readOnly
                                                style={{
                                                    width: "100%",
                                                    height: "38px",
                                                    padding: '6px 8px',
                                                    borderRadius: '4px',
                                                    border: '1px solid #ced4da',
                                                    backgroundColor: '#e9ecef'
                                                }}
                                            />
                                        </Col>

                                        {isOnlineProduct(prod.product_id) && (
                                            <Col xl={1}>
                                                <div className="d-flex flex-column h-100">
                                                    <label className="fw-bold mb-1">{t("Online Price")}</label>
                                                    <input
                                                        type="number"
                                                        name="online_price"
                                                        min={0}
                                                        step="0.01"
                                                        value={prod.online_price}
                                                        placeholder={t("Online")}
                                                        onChange={(e) => handleProductChange(index, e)}
                                                        style={{
                                                            width: "100%",
                                                            height: "38px",
                                                            padding: '6px 8px',
                                                            borderRadius: '4px',
                                                            border: '1px solid #ced4da'
                                                        }}
                                                    />
                                                    <label style={{ fontSize: "0.7rem", marginTop: "4px", cursor: "pointer" }}>
                                                        <input
                                                            type="checkbox"
                                                            style={{ marginRight: "3px" }}
                                                            onChange={async (e) => {
                                                                if (e.target.checked && prod.product_id) {
                                                                    await updatePreviousPrice(index, 'online_price', prod.product_id);
                                                                }
                                                            }}
                                                        />
                                                        {t("Prev")}
                                                    </label>
                                                </div>
                                            </Col>
                                        )}
                                    </Row>
                                </Card>
                            ))}

                            <Row className="mb-4 align-items-start">
                                <Col>
                                    <ButtonComponent
                                        className="btn btn-primary"
                                        onClick={addProductRow}
                                        text={t("Add Product")}
                                    />
                                </Col>

                                <Col md="auto">
                                    <div
                                        style={{
                                            minWidth: '260px',
                                            padding: '12px 15px',
                                        }}
                                    >
                                        {/* Subtotal */}
                                        <div className="d-flex justify-content-between mb-2">
                                            <strong>{t("Subtotal")}</strong>
                                            <span>
                                                {grandTotal - (Number(formData.tax) || 0)}
                                            </span>
                                        </div>

                                        {/* Tax */}
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <label
                                                style={{
                                                    marginBottom: 0,
                                                    fontWeight: 600,
                                                    fontSize: '14px'
                                                }}
                                            >
                                                {t("Tax Amount")}
                                            </label>

                                            <input
                                                type="number"
                                                name="tax"
                                                value={formData.tax}
                                                onChange={handleChange}
                                                placeholder="0"
                                                min="0"
                                                step="0.01"
                                                className="form-control"
                                                style={{
                                                    width: '100px',
                                                    height: '32px',
                                                    fontSize: '14px'
                                                }}
                                            />
                                        </div>

                                        {/* Grand Total */}
                                        <div
                                            className="d-flex justify-content-between"
                                            style={{
                                                paddingTop: '10px',
                                                fontWeight: 'bold',
                                                fontSize: '18px'
                                            }}
                                        >
                                            <span>{t("Grand Total")}</span>
                                            <span>{grandTotal}</span>
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            <Row className="mb-4">
                                <Col xl={6}>
                                    <LabelTextareaComponent
                                        label={t("Notes")}
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleChange}
                                    />
                                </Col>
                                <Col xl={6}>
                                    <h5 className="mb-3">{t("Payments")}</h5>
                                    {formData.payments.map((pay, index) => (
                                        <Card
                                            key={index}
                                            className="mb-3 p-3"
                                            style={{ borderColor: "#ddd", backgroundColor: "#f9f9f9", borderRadius: "6px" }}
                                        >
                                            <Row className="align-items-center">
                                                <Col xs={4}>
                                                    <label className="fw-bold mb-1">{t("Payment Method")}</label>
                                                    <select
                                                        name="payment_method_id"
                                                        value={pay.payment_method_id}
                                                        onChange={(e) => handlePaymentChange(index, e)}
                                                        className="form-control"
                                                    >
                                                        <option value="">{t("Select Method")}</option>
                                                        {paymentMethods.map((method) => (
                                                            <option key={method.id} value={method.id}>
                                                                {method.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </Col>
                                                <Col xs={3}>
                                                    <label className="fw-bold mb-1">{t("Amount")}</label>
                                                    <input
                                                        type="number"
                                                        name="amount"
                                                        value={pay.amount}
                                                        placeholder={t("Enter amount")}
                                                        onChange={(e) => handlePaymentChange(index, e)}
                                                        className="form-control"
                                                    />
                                                </Col>
                                                <Col xs={4}>
                                                    <label className="fw-bold mb-1">{t("Payment Date")} <span className="text-danger">*</span></label>
                                                    <input
                                                        type="date"
                                                        name="payment_date"
                                                        value={pay.payment_date || ''}
                                                        onChange={(e) => handlePaymentChange(index, e)}
                                                        className="form-control"
                                                    />
                                                </Col>
                                                <Col xs={1} className="text-center">
                                                    <ButtonComponent
                                                        className="btn btn-danger btn-sm mt-4"
                                                        onClick={() => removePaymentRow(index)}
                                                        text="X"
                                                        disabled={formData.payments.length === 1}
                                                    />
                                                </Col>
                                            </Row>
                                        </Card>
                                    ))}
                                    <ButtonComponent
                                        className="btn btn-primary btn-sm mt-1"
                                        onClick={addPaymentRow}
                                        text={t("Add Payment")}
                                        disabled={getRemainingAmount() <= 0}
                                    />
                                </Col>
                            </Row>

                            <ButtonComponent
                                className="mc-btn primary w-100 mt-3"
                                onClick={handleSubmit}
                                text={t("Create Purchase")}
                            />
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Vendor Create Modal */}
            <Modal show={showVendorModal} onHide={() => setShowVendorModal(false)} size="lg" centered>
                <Modal.Header closeButton className="p-2">
                    <Modal.Title>{t('Add New Vendor')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleVendorSubmit}>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('name')} <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={vendorForm.name}
                                        onChange={handleVendorChange}
                                        isInvalid={!!vendorErrors.name}
                                        placeholder={t('Enter vendor name')}
                                    />
                                    <Form.Control.Feedback type="invalid">{vendorErrors.name}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('email')}</Form.Label>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={vendorForm.email}
                                        onChange={handleVendorChange}
                                        isInvalid={!!vendorErrors.email}
                                        placeholder={t('Enter email address')}
                                    />
                                    <Form.Control.Feedback type="invalid">{vendorErrors.email}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('phone')}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="phone"
                                        value={vendorForm.phone}
                                        onChange={handleVendorChange}
                                        isInvalid={!!vendorErrors.phone}
                                        placeholder={t('Enter phone number')}
                                    />
                                    <Form.Control.Feedback type="invalid">{vendorErrors.phone}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('image')}</Form.Label>
                                    <Form.Control
                                        type="file"
                                        name="img"
                                        onChange={handleVendorChange}
                                        accept="image/*"
                                        isInvalid={!!vendorErrors.img}
                                    />
                                    <Form.Control.Feedback type="invalid">{vendorErrors.img}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('address')}</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                name="address"
                                value={vendorForm.address}
                                onChange={handleVendorChange}
                                isInvalid={!!vendorErrors.address}
                                placeholder={t('Enter address')}
                            />
                            <Form.Control.Feedback type="invalid">{vendorErrors.address}</Form.Control.Feedback>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="p-2">
                        <ButtonComponent className="btn btn-secondary" onClick={() => setShowVendorModal(false)}>
                            {t('cancel')}
                        </ButtonComponent>
                        <ButtonComponent className="btn btn-primary" type="submit" disabled={vendorLoading}>
                            {vendorLoading ? <><Spinner animation="border" size="sm" className="me-2" />{t('saving')}...</> : t('save')}
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Product Create Modal */}
            <Modal show={showProductModal} onHide={() => setShowProductModal(false)} size="lg" centered>
                <Modal.Header closeButton className="p-2">
                    <Modal.Title>{t('Add New Product')}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleProductSubmit}>
                    <Modal.Body>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('Product Name')} <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={productForm.name}
                                        onChange={handleProductFormChange}
                                        isInvalid={!!productErrors.name}
                                        placeholder={t('Enter product name')}
                                    />
                                    <Form.Control.Feedback type="invalid">{productErrors.name}</Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('Category')} <span className="text-danger">*</span></Form.Label>
                                    <Select
                                        options={categories.map((category) => ({
                                            value: category.id,
                                            label: category.name,
                                        }))}
                                        value={
                                            categories
                                                .map((category) => ({
                                                    value: category.id,
                                                    label: category.name,
                                                }))
                                                .find((option) => option.value == productForm.cat_id) || null
                                        }
                                        onChange={(selectedOption) => {
                                            setProductForm({
                                                ...productForm,
                                                cat_id: selectedOption ? selectedOption.value : '',
                                            });
                                            setProductErrors({ ...productErrors, cat_id: '' });
                                        }}
                                        placeholder={t('Select Category')}
                                        isClearable
                                    />
                                    {productErrors.cat_id && (
                                        <div className="invalid-feedback d-block">{productErrors.cat_id}</div>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>{t('Vendor')} <span className="text-danger">*</span></Form.Label>
                                    <Select
                                        options={vendors.map((vendor) => ({
                                            value: vendor.id,
                                            label: vendor.company_name || vendor.name,
                                        }))}
                                        value={
                                            vendors
                                                .map((vendor) => ({
                                                    value: vendor.id,
                                                    label: vendor.company_name || vendor.name,
                                                }))
                                                .find((option) => option.value == productForm.vendor_id) || null
                                        }
                                        onChange={(selectedOption) => {
                                            setProductForm({
                                                ...productForm,
                                                vendor_id: selectedOption ? selectedOption.value : '',
                                            });
                                            setProductErrors({ ...productErrors, vendor_id: '' });
                                        }}
                                        placeholder={t('Select Vendor')}
                                        isClearable
                                        isDisabled={true}
                                    />
                                    {productErrors.vendor_id && (
                                        <div className="invalid-feedback d-block">{productErrors.vendor_id}</div>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>
                    </Modal.Body>
                    <Modal.Footer className="p-2">
                        <ButtonComponent className="btn btn-secondary" onClick={() => setShowProductModal(false)}>
                            {t('cancel')}
                        </ButtonComponent>
                        <ButtonComponent className="btn btn-primary" type="submit" disabled={productLoading}>
                            {productLoading ? <><Spinner animation="border" size="sm" className="me-2" />{t('saving')}...</> : t('save')}
                        </ButtonComponent>
                    </Modal.Footer>
                </Form>
            </Modal>
        </PageLayout>
    );
}